import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  PhoneIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  MapPinIcon,
  TruckIcon,
  CalendarDaysIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { FaFacebookF } from 'react-icons/fa';

import * as API from '../../api';
import BookingSteps from '../../shared/components/BookingSteps';
import orderNeedsPayment from '../../shared/utils/orderNeedsPayment';

/**
 * Checkout — auth gate + order placement. Premium redesign.
 *
 * ALL logic preserved verbatim from the legacy page: changeDiscount math,
 * API.Auth() gate, submitLogin / submitRegister (reload on success), and
 * submitOrder (builds the type-specific form with qty_{priceId}_{eventId}
 * keys + coupon + gift, calls API.Order, clears localStorage, stores
 * order_response, then routes to /thankyou). Only the presentation changed.
 */

const Checkout = () => {
  const { t } = useTranslation();
  localStorage.setItem('intended', '/checkout');
  const history = useHistory();
  const [gift] = React.useState(JSON.parse(localStorage.getItem('gift')) || {});
  const [orderBooking] = React.useState(JSON.parse(localStorage.getItem('order_booking')) || {});

  const [totalPrice, setTotalPrice] = React.useState(orderBooking.price_total);
  const [totalPricePay, setTotalPricePay] = React.useState(orderBooking.price_for_pay);
  const [totalPriceRest, setTotalPriceRest] = React.useState(
    orderBooking.price_total - orderBooking.price_for_pay
  );

  const [authStatus, setAuthStatus] = React.useState(false);
  const [auth, setAuth] = React.useState({});
  const [loginFormStatus, setLoginFormStatus] = React.useState(false);
  const [signupFormStatus, setSignupFormStatus] = React.useState(false);

  const [loadingLoginStatus, setLoadingLoginStatus] = React.useState(false);
  const [loadingRegisterStatus, setLoadingRegisterStatus] = React.useState(false);
  const [loadingOrderingStatus, setLoadingOrderingStatus] = React.useState(false);

  const [loginHelper, setLoginHelper] = React.useState({ type: '', message: '' });
  const [registerHelper, setRegisterHelper] = React.useState({ type: '', message: '' });
  const [orderHelper, setOrderHelper] = React.useState({ type: '', message: '' });

  // Field-level helpers are kept separate per form so a login error never
  // bleeds into the signup form (and vice-versa).
  const [loginEmailHelper, setLoginEmailHelper] = React.useState('');
  const [loginPasswordHelper, setLoginPasswordHelper] = React.useState('');
  const [nameHelper, setNameHelper] = React.useState('');
  const [phoneHelper, setPhoneHelper] = React.useState('');
  const [emailHelper, setEmailHelper] = React.useState('');
  const [passwordHelper, setPasswordHelper] = React.useState('');
  const [password_confirmationHelper, setPasswordConfirmationHelper] = React.useState('');

  const [buttonStatus] = React.useState(true);
  const [loadingAuthCheck, setLoadingAuthCheck] = React.useState(false);
  const [facebookLoginUrl, setFacebookLoginUrl] = React.useState('');
  const [check_in] = React.useState(orderBooking?.sejour_dates?.check_in ?? new Date());
  const [check_out] = React.useState(orderBooking?.sejour_dates?.check_out ?? new Date());

  // ---- logic (preserved) ---------------------------------------------------

  const changeDiscount = (user = {}) => {
    let _percentageSettings = 100 - orderBooking.event.percentage_paid;
    let _discount =
      Object.keys(user).length && parseInt(user.customer_discount)
        ? parseInt(user.customer_discount)
        : orderBooking.coupon.value ?? 0;
    let _total = totalPrice - (totalPrice / 100) * _discount;
    let _forPay = _total - (_total / 100) * _percentageSettings;
    let _forRest = _total - _forPay;
    setTotalPrice(_total);
    setTotalPricePay(_forPay);
    setTotalPriceRest(_forRest);
  };

  const totalPlace = (_prices) => {
    let places = 0;
    _prices.forEach((price) => {
      places = parseInt(places) + parseInt(price.qt);
    });
    return places;
  };

  const submitLogin = (event) => {
    event.preventDefault();
    setLoadingLoginStatus(true);
    setLoginHelper({ type: '', message: '' });
    setLoginEmailHelper('');
    setLoginPasswordHelper('');

    const formData = new FormData(event.target);
    const form = { email: formData.get('email'), password: formData.get('password') };

    API.SignIn(form)
      .then((res) => {
        setAuthStatus(true);
        setLoginHelper({ type: 'success', message: t('checkout.loginSuccess', { name: res.data.user.name }) });
        event.target.reset();
        setAuth(res.data.user);
        changeDiscount(res.data.user);
        window.location.reload();
      })
      .catch((err) => {
        if (err.status === 422) {
          setLoginHelper({ type: 'error', message: err.data.message });
          if (err.data.errors.email) setLoginEmailHelper(err.data.errors.email);
          if (err.data.errors.password) setLoginPasswordHelper(err.data.errors.password);
        }
        if (err.status === 401) setLoginHelper({ type: 'error', message: err.data.errors });
        if (err.status === 500) setLoginHelper({ type: 'error', message: t('checkout.serverError') });
      })
      .then(() => setLoadingLoginStatus(false));
  };

  const submitRegister = (event) => {
    event.preventDefault();
    setLoadingRegisterStatus(true);
    setRegisterHelper({ type: '', message: '' });
    setNameHelper('');
    setEmailHelper('');
    setPhoneHelper('');
    setPasswordHelper('');
    setPasswordConfirmationHelper('');

    const formData = new FormData(event.target);
    const form = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      password: formData.get('password'),
      password_confirmation: formData.get('password_confirmation'),
      type: 'user',
      privacy_policy: true,
    };
    if (form.password && form.password !== form.password_confirmation) {
      setPasswordConfirmationHelper(t('checkout.passwordMismatch'));
    }

    API.SignUp(form)
      .then((res) => {
        setAuthStatus(true);
        setRegisterHelper({ type: 'success', message: t('checkout.loginSuccess', { name: res.data.user.name }) });
        event.target.reset();
        setAuth(res.data.user);
        changeDiscount(res.data.user);
        window.location.reload();
      })
      .catch((err) => {
        if (err.status === 422) {
          setRegisterHelper({ type: 'error', message: err.data.message });
          if (err.data.errors.name) setNameHelper(err.data.errors.name);
          if (err.data.errors.email) setEmailHelper(err.data.errors.email);
          if (err.data.errors.phone) setPhoneHelper(err.data.errors.phone);
          if (err.data.errors.password) setPasswordHelper(err.data.errors.password);
          if (err.data.errors.password_confirmation)
            setPasswordConfirmationHelper(err.data.errors.password_confirmation);
        }
        if (err.status === 401) setRegisterHelper({ type: 'error', message: err.data.errors });
        if (err.status === 500) setRegisterHelper({ type: 'error', message: t('checkout.serverError') });
      })
      .then(() => setLoadingRegisterStatus(false));
  };

  const submitOrder = () => {
    setLoadingOrderingStatus(true);
    let _event = orderBooking.event;
    let _form = {};
    if (_event.type === 'Activitie') {
      _form = {
        event_id: _event.id,
        depart_city: '1',
        date_activity: orderBooking.date_activity,
        time_activity: orderBooking.time_activity,
      };
    } else if (_event.type === 'Event') {
      _form = { event_id: _event.id, depart_city: orderBooking.city.trim() };
      if (orderBooking.roader && orderBooking.roader !== 1) {
        Object.assign(_form, { roader: orderBooking.roader });
      } else {
        Object.assign(_form, { roader: 0 });
      }
    } else if (_event.type === 'Sejour') {
      _form = {
        event_id: _event.id,
        depart_city: '1',
        check_in: orderBooking?.sejour_dates?.check_in ?? new Date(),
        check_out: orderBooking?.sejour_dates?.check_out ?? new Date(),
      };
    } else {
      _form = { event_id: _event.id, depart_city: '1' };
    }

    orderBooking.prices.forEach((price) => {
      if (price.qt > 0) {
        let _key = `qty_${price.id}_${_event.id}`;
        Object.assign(_form, { [_key]: parseInt(price.qt) });
      }
    });
    if (Object.keys(orderBooking.coupon).length && !parseInt(auth.customer_discount)) {
      Object.assign(_form, {
        coupon_name: orderBooking.coupon.name,
        coupon_value: orderBooking.coupon.value,
      });
    }
    if (Object.keys(gift).length && gift.gift_email) {
      Object.assign(_form, gift);
    }

    API.Order(_form)
      .then((res) => {
        setOrderHelper({ type: 'success', message: res.data.message });
        localStorage.removeItem('intended');
        localStorage.removeItem('gift');
        localStorage.removeItem('order');
        localStorage.removeItem('order_booking');
        localStorage.setItem('order_response', JSON.stringify(res.data.data));
        history.push(orderNeedsPayment(res.data.data) ? '/payment' : '/thankyou');
      })
      .catch(() => {})
      .then(() => setLoadingOrderingStatus(false));
  };

  React.useEffect(() => {
    API.socialLoginLink('facebook').then((res) => setFacebookLoginUrl(res.url));

    if (!Object.keys(orderBooking).length) {
      history.replace('/');
      return;
    }
    document.title = API.APP_NAME + ' - Checkout ';

    setLoadingAuthCheck(true);
    API.Auth()
      .then((res) => {
        setAuthStatus(true);
        setLoginFormStatus(false);
        setSignupFormStatus(false);
        setAuth(res.user);
        changeDiscount(res.user);
      })
      .catch(() => {
        setLoginFormStatus(true);
        setSignupFormStatus(true);
        changeDiscount();
      })
      .then(() => setLoadingAuthCheck(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [0]);

  if (!Object.keys(orderBooking).length) return null;

  const event = orderBooking.event;
  const showForms = (loginFormStatus || signupFormStatus) && !authStatus;
  const zeroPaid = API.priceFormat(totalPricePay) !== '0,00 MAD';
  const guests = totalPlace(orderBooking.prices);
  const userDiscount = authStatus && parseInt(auth.customer_discount) > 0 ? auth.customer_discount : null;
  const couponActive =
    Object.keys(orderBooking.coupon).length && orderBooking.coupon.value && !parseInt(auth.customer_discount);

  // ---- render --------------------------------------------------------------

  return (
    <div className="bg-surface-muted pb-12">
      {/* Header */}
      <header className="border-b border-slate-100 bg-surface">
        <div className="mx-auto w-full max-w-container px-4 py-7 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 text-xs text-ink-subtle" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-brand-500">
              {t('checkout.breadcrumbHome')}
            </Link>
          </nav>
          <h1 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
            {t('checkout.title')}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">{t('checkout.subtitle')}</p>
          <BookingSteps active="details" hasDeposit={Math.round(totalPricePay) > 0} />
        </div>
      </header>

      <div className="mx-auto w-full max-w-container px-4 py-8 sm:px-6 lg:px-8">
        <div
          className={`grid grid-cols-1 gap-8 ${
            showForms ? 'lg:grid-cols-[minmax(0,1fr)_400px]' : 'lg:grid-cols-[minmax(0,1fr)_400px]'
          }`}
        >
          {/* ---- Left: auth (only when logged out) ---- */}
          <div className="space-y-6">
            {loadingAuthCheck ? (
              <div className="flex h-40 items-center justify-center rounded-2xl border border-slate-100 bg-white shadow-card">
                <Spinner tone="brand" />
              </div>
            ) : showForms ? (
              <>
                {/* Facebook */}
                <a
                  href={facebookLoginUrl}
                  className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-[#1877F2] px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                >
                  <FaFacebookF className="h-4 w-4" />
                  {t('checkout.withFacebook')}
                </a>

                <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-ink-subtle">
                  <span className="h-px flex-1 bg-slate-200" />
                  {t('checkout.or')}
                  <span className="h-px flex-1 bg-slate-200" />
                </div>

                {/* Login */}
                <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card sm:p-6">
                  <h2 className="mb-4 font-display text-lg font-semibold text-ink">
                    {t('checkout.loginTitle')}
                  </h2>
                  {loginHelper.message ? (
                    <FormAlert type={loginHelper.type} html={loginHelper.message} />
                  ) : null}
                  <form onSubmit={submitLogin} method="POST" className="space-y-3">
                    <TextInput name="email" type="email" placeholder={t('checkout.email')} icon={EnvelopeIcon} error={loginEmailHelper} disabled={loadingLoginStatus} />
                    <TextInput name="password" type="password" placeholder={t('checkout.password')} icon={LockClosedIcon} error={loginPasswordHelper} disabled={loadingLoginStatus} />
                    <div className="text-right rtl:text-left">
                      <Link to="/reset" className="text-xs font-medium text-brand-500 hover:underline">
                        {t('checkout.forgotPassword')}
                      </Link>
                    </div>
                    <SubmitButton loading={loadingLoginStatus} label={t('checkout.loginBtn')} />
                  </form>
                </section>

                {/* Signup */}
                <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card sm:p-6">
                  <h2 className="mb-4 font-display text-lg font-semibold text-ink">
                    {t('checkout.signupTitle')}
                  </h2>
                  {registerHelper.message ? (
                    <FormAlert type={registerHelper.type} html={registerHelper.message} />
                  ) : null}
                  <form onSubmit={submitRegister} method="POST" className="space-y-3">
                    <TextInput name="name" placeholder={t('checkout.name')} icon={UserIcon} error={nameHelper} disabled={loadingRegisterStatus} />
                    <TextInput name="email" type="email" placeholder={t('checkout.email')} icon={EnvelopeIcon} error={emailHelper} disabled={loadingRegisterStatus} />
                    <TextInput name="phone" type="tel" placeholder={t('checkout.phone')} icon={PhoneIcon} error={phoneHelper} disabled={loadingRegisterStatus} />
                    <TextInput name="password" type="password" placeholder={t('checkout.password')} icon={LockClosedIcon} error={passwordHelper} disabled={loadingRegisterStatus} />
                    <TextInput name="password_confirmation" type="password" placeholder={t('checkout.passwordConfirm')} icon={LockClosedIcon} error={password_confirmationHelper} disabled={loadingRegisterStatus} />
                    <SubmitButton loading={loadingRegisterStatus} label={t('checkout.signupBtn')} />
                  </form>
                </section>
              </>
            ) : (
              // Logged in — show a confirmation hint card on the left.
              <section className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-5">
                <CheckCircleIcon className="h-6 w-6 shrink-0 text-emerald-600" />
                <div>
                  <p className="font-semibold text-ink">{auth.name}</p>
                  <p className="text-sm text-ink-muted">{auth.email}</p>
                </div>
              </section>
            )}
          </div>

          {/* ---- Right: order summary + confirm ---- */}
          <aside>
            <div className="lg:sticky lg:top-24">
              <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card">
                <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
                  {event.image ? (
                    <img src={event.image} alt={event.title} className="h-12 w-12 rounded-lg object-cover" />
                  ) : null}
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-ink">{event.title}</p>
                    <p className="text-xs text-ink-subtle">{t('checkout.summaryTitle')}</p>
                  </div>
                </div>

                <div className="space-y-2.5 px-5 py-4 text-sm">
                  {event.type === 'Sejour' ? (
                    <>
                      <Row icon={CalendarDaysIcon} label={t('checkout.checkIn')} value={new Date(check_in).toLocaleDateString('fr-FR')} />
                      <Row icon={CalendarDaysIcon} label={t('checkout.checkOut')} value={new Date(check_out).toLocaleDateString('fr-FR')} />
                    </>
                  ) : (
                    <Row icon={CalendarDaysIcon} label={t('checkout.date')} value={orderBooking.date_activity || new Date().toLocaleDateString('fr-FR')} />
                  )}
                  {orderBooking.city ? (
                    <Row icon={MapPinIcon} label={t('checkout.departureCity')} value={orderBooking.city} />
                  ) : null}
                  {orderBooking.roader && orderBooking.roaders?.find((r) => r.id === orderBooking.roader) ? (
                    <Row icon={TruckIcon} label={t('checkout.carpool')} value={orderBooking.roaders.find((r) => r.id === orderBooking.roader).name} />
                  ) : null}
                  <Row icon={UserGroupIcon} label={t('checkout.guests')} value={guests} />
                </div>

                {/* Totals */}
                <div className="space-y-2 border-t border-slate-100 px-5 py-4 text-sm">
                  <div className="flex items-center justify-between text-ink-muted">
                    <span>{t('checkout.subtotal')}</span>
                    <bdi>{API.priceFormat(orderBooking.price_total)}</bdi>
                  </div>
                  {totalPrice !== orderBooking.price_total ? (
                    <div className="flex items-center justify-between text-emerald-600">
                      <span>
                        {t('checkout.discount', {
                          value: userDiscount || orderBooking.coupon.value,
                        })}
                      </span>
                      <bdi>{API.priceFormat(totalPrice - orderBooking.price_total)}</bdi>
                    </div>
                  ) : null}
                  <div className="flex items-center justify-between pt-1">
                    <span className="font-display text-base font-semibold text-ink">{t('checkout.total')}</span>
                    <bdi className="font-display text-xl font-bold text-brand-500">{API.priceFormat(totalPrice)}</bdi>
                  </div>
                  {zeroPaid ? (
                    <div className="mt-2 space-y-1 rounded-xl bg-surface-muted p-3 text-xs">
                      <div className="flex items-center justify-between text-ink">
                        <span>{t('checkout.advanceToPay')}</span>
                        <bdi className="font-semibold">{API.priceFormat(totalPricePay)}</bdi>
                      </div>
                      <div className="flex items-center justify-between text-ink-subtle">
                        <span>{t('checkout.restToPay')}</span>
                        <bdi>{API.priceFormat(totalPriceRest)}</bdi>
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Confirm */}
                <div className="border-t border-slate-100 px-5 py-4">
                  {loadingAuthCheck ? (
                    <div className="flex justify-center">
                      <Spinner tone="brand" />
                    </div>
                  ) : authStatus && buttonStatus ? (
                    <button
                      type="button"
                      onClick={submitOrder}
                      disabled={loadingOrderingStatus}
                      className="flex w-full items-center justify-center rounded-xl bg-accent-500 px-4 py-3.5 text-sm font-bold text-brand-900 shadow-card transition-all duration-200 hover:bg-accent-400 hover:shadow-card-hover disabled:opacity-60"
                    >
                      {loadingOrderingStatus ? <Spinner tone="dark" /> : t('checkout.reserve')}
                    </button>
                  ) : (
                    <p className="text-center text-xs text-ink-subtle">{t('checkout.subtitle')}</p>
                  )}
                </div>
              </div>

              {/* Notices */}
              {!loadingAuthCheck && authStatus && buttonStatus ? (
                <p className="mt-3 text-xs leading-relaxed text-ink-subtle">{t('checkout.processingNote')}</p>
              ) : null}

              {couponActive ? (
                <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs text-emerald-700">
                  {t('checkout.couponNotice', { value: orderBooking.coupon.value, name: orderBooking.coupon.name })}
                </div>
              ) : null}

              {userDiscount ? (
                <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs text-emerald-700">
                  {t('checkout.userDiscountNotice', { value: userDiscount })}
                  {Object.keys(orderBooking.coupon).length ? (
                    <span className="mt-1 block">{t('checkout.userDiscountOverridesCoupon')}</span>
                  ) : null}
                </div>
              ) : null}

              {orderHelper.message ? (
                <div className="mt-3">
                  <FormAlert type={orderHelper.type} html={orderHelper.message} />
                </div>
              ) : null}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

// ---- presentational helpers -------------------------------------------------

/**
 * Loading spinner. `tone` controls the ring colour so it stays visible on
 * whatever background it sits on:
 *   - 'light' (default) — white ring, for coloured buttons (brand/accent)
 *   - 'brand'           — brand-blue ring, for white/neutral backgrounds
 *   - 'dark'            — navy ring, for the yellow accent button
 */
function Spinner({ tone = 'light' }) {
  const ring =
    tone === 'brand'
      ? 'border-brand-500/30 border-t-brand-500'
      : tone === 'dark'
      ? 'border-brand-900/30 border-t-brand-900'
      : 'border-white/40 border-t-white';
  return <span className={`inline-block h-5 w-5 animate-spin rounded-full border-2 ${ring}`} />;
}

function TextInput({ name, type = 'text', placeholder, icon: Icon, error, disabled }) {
  return (
    <div>
      <div className="relative">
        <input
          name={name}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-3.5 pr-10 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50 rtl:pl-10 rtl:pr-3.5"
        />
        {Icon ? (
          <Icon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle rtl:right-auto rtl:left-3" />
        ) : null}
      </div>
      {error ? <p className="mt-1 text-xs text-rose-500">{error}</p> : null}
    </div>
  );
}

function SubmitButton({ loading, label }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="flex w-full items-center justify-center rounded-xl bg-brand-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60"
    >
      {loading ? <Spinner /> : label}
    </button>
  );
}

function FormAlert({ type, html }) {
  const ok = type === 'success';
  const Icon = ok ? CheckCircleIcon : ExclamationCircleIcon;
  return (
    <div
      className={`mb-3 flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-sm ${
        ok ? 'border-emerald-100 bg-emerald-50 text-emerald-700' : 'border-rose-100 bg-rose-50 text-rose-600'
      }`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-ink-muted">
      <span className="inline-flex items-center gap-2">
        <Icon className="h-4 w-4 text-brand-500" />
        {label}
      </span>
      <span className="text-right font-medium text-ink rtl:text-left">{value}</span>
    </div>
  );
}

export default Checkout;
